/** Pure helpers of the auth layer (tested in Node). */

/** Inactivity before the device locks, when the session has a PIN (docs/08 §7). */
export const LOCK_AFTER_MS = 10 * 60 * 1000;

/** Pages outside the shell: never used as a return path. */
const AUTH_PAGES = ['/login', '/lock', '/cambio-password'];

/** True when the device must lock: PIN set and no activity for `LOCK_AFTER_MS`. */
export function isLockDue(
  lastActivityMs: number | null,
  nowMs: number,
  hasPin: boolean,
  lockAfterMs: number = LOCK_AFTER_MS,
): boolean {
  if (!hasPin || lastActivityMs == null) return false;
  return nowMs - lastActivityMs >= lockAfterMs;
}

/**
 * Return path from a `?next=` parameter: only same-origin paths ("/x"), never "//host" or an auth
 * page (open redirect and loops), nor a backslash or a control character anywhere (browsers read
 * "\" as "/" and drop tabs and newlines: "/\t/host" is "//host"). Falls back to "/".
 */
export function safeNextPath(raw: string | null | undefined): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || /[\p{Cc}\\]/u.test(raw)) return '/';
  const path = raw.split(/[?#]/, 1)[0] ?? '/';
  if (AUTH_PAGES.includes(path)) return '/';
  return raw;
}

/** `/login?next=…` (or /lock, /cambio-password) for the current location. */
export function withNext(page: string, location: { pathname: string; search?: string }): string {
  const current = `${location.pathname}${location.search ?? ''}`;
  const next = safeNextPath(current);
  return next === '/' ? page : `${page}?next=${encodeURIComponent(next)}`;
}
